import { useState, useMemo } from 'react';
import { FormField as FormFieldType, FormFieldGroup as FormFieldGroupType, FormWizardStep } from '../../Types/forms';
import { useFormContext } from './FormContext';
import FormField from './FormField';
import FormFieldGroup from './FormFieldGroup';
import './FormWizard.scss';

type FormWizardProps = {
	steps: FormWizardStep[];
	fields: FormFieldType[];
	groups: FormFieldGroupType[];
};

const FormWizard = ({ steps, fields, groups }: FormWizardProps) => {
	const sortedSteps = useMemo(() => [...steps].sort((a, b) => a.order - b.order), [steps]);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const { errors, hiddenFields } = useFormContext();

	const currentStep = sortedSteps[currentStepIndex];
	if (!currentStep) return null;

	const stepFields = fields.filter((f) => f.wizardStepKey === currentStep.key);
	const stepGroups = groups.filter((g) => g.settings.wizardStepKey === currentStep.key);

	// Fields not in any group within this wizard step
	const groupKeys = new Set(stepGroups.map((g) => g.key));
	const ungroupedFields = stepFields.filter((f) => !f.groupKey || !groupKeys.has(f.groupKey));

	const isFirstStep = currentStepIndex === 0;
	const isLastStep = currentStepIndex === sortedSteps.length - 1;

	const canProceed = () => {
		return !stepFields.some((f) => !hiddenFields.has(f.key) && errors[f.key]);
	};

	const handleNext = () => {
		if (!isLastStep && canProceed()) {
			setCurrentStepIndex(currentStepIndex + 1);
		}
	};

	const handlePrev = () => {
		if (!isFirstStep) {
			setCurrentStepIndex(currentStepIndex - 1);
		}
	};

	return (
		<div className='biz-form-wizard'>
			<div className='biz-form-wizard__steps'>
				{sortedSteps.map((step, index) => (
					<div
						key={step.key}
						className={`biz-form-wizard__step ${index === currentStepIndex ? 'biz-form-wizard__step--active' : ''} ${index < currentStepIndex ? 'biz-form-wizard__step--completed' : ''}`}
					>
						<span className='biz-form-wizard__step-number'>{index + 1}</span>
						{step.name && <span className='biz-form-wizard__step-name'>{step.name}</span>}
					</div>
				))}
			</div>

			<div className='biz-form-wizard__content'>
				{ungroupedFields.map((field) => (
					<FormField key={field.key} field={field} />
				))}

				{stepGroups
					.sort((a, b) => a.order - b.order)
					.map((group) => {
						const groupFields = stepFields.filter((f) => f.groupKey === group.key);
						return <FormFieldGroup key={group.key} group={group} fields={groupFields} />;
					})}
			</div>

			<div className='biz-form-wizard__nav'>
				{!isFirstStep && (
					<button type='button' className='biz-btn biz-form-wizard__prev' onClick={handlePrev}>
						&#8592;
					</button>
				)}
				{!isLastStep && (
					<button type='button' className='biz-btn biz-primary biz-form-wizard__next' onClick={handleNext}>
						&#8594;
					</button>
				)}
			</div>
		</div>
	);
};

export default FormWizard;
